import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { content } from '../data/content';
import { links } from '../data/links';

const Navbar = ({ theme, toggleTheme, language, toggleLanguage }) => {
    const location = useLocation();
    const t = content[language].navbar;
    const [menuOpen, setMenuOpen] = useState(false);
    const menuButtonRef = useRef(null);

    // Close the mobile menu whenever the route changes.
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Esc closes the menu and returns focus to the toggle button.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setMenuOpen(false);
                menuButtonRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [menuOpen]);

    const navLinks = [
        { name: t.home, path: '/' },
        { name: t.experience, path: '/experience' },
        { name: t.projects, path: '/projects' },
        { name: t.publications, path: '/publications' },
        { name: t.awards, path: '/awards' },
    ];

    return (
        <nav className="fixed top-0 left-0 w-full z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
            <div className="container mx-auto px-4 py-3 flex justify-between items-center">
                <Link to="/" className="flex items-center gap-2 group">
                    <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-blue-500 group-hover:border-blue-400 transition-colors">
                        <img src={`${process.env.PUBLIC_URL}/ben_logo.jpg`} alt="Logo" className="w-full h-full object-cover" />
                    </div>
                    <span className="font-bold text-xl text-gray-800 dark:text-white group-hover:text-blue-500 transition-colors">
                        {language === 'en' ? 'Ben Yeh' : '葉舜斌'}
                    </span>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center gap-6">
                    {navLinks.map((link) => {
                        const active = location.pathname === link.path;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                aria-current={active ? 'page' : undefined}
                                className={`relative font-medium text-sm transition-colors duration-300 ${active
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-300'
                                    }`}
                            >
                                {link.name}
                                {active && (
                                    <span className="absolute left-0 top-full block h-[2px] w-full bg-blue-600 dark:bg-blue-400 mt-1" />
                                )}
                            </Link>
                        );
                    })}
                    <a
                        href={links.resume}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-500 hover:text-white transition-colors"
                    >
                        <i className="fas fa-file-arrow-down"></i> {t.resume}
                    </a>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-3">
                    {/* Resume (always visible, for mobile reachability) */}
                    <a
                        href={links.resume}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t.resume}
                        className="md:hidden p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                        <i className="fas fa-file-arrow-down"></i>
                    </a>

                    {/* Language Toggle */}
                    <button
                        onClick={() => toggleLanguage(language === 'en' ? 'zh' : 'en')}
                        className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                        {language === 'en' ? '中文' : 'EN'}
                    </button>

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                        className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                        {theme === 'light' ? (
                            <i className="fas fa-moon"></i>
                        ) : (
                            <i className="fas fa-sun"></i>
                        )}
                    </button>

                    {/* Mobile menu toggle */}
                    <button
                        ref={menuButtonRef}
                        onClick={() => setMenuOpen((open) => !open)}
                        aria-expanded={menuOpen}
                        aria-controls="mobile-menu"
                        aria-label={menuOpen ? t.menuClose : t.menuOpen}
                        className="md:hidden p-2 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                        <i className={`fas ${menuOpen ? 'fa-xmark' : 'fa-bars'}`} aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            {/* Mobile Navigation */}
            <div
                id="mobile-menu"
                hidden={!menuOpen}
                className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95"
            >
                <ul className="container mx-auto px-4 py-2 flex flex-col">
                    {navLinks.map((link) => {
                        const active = location.pathname === link.path;
                        return (
                            <li key={link.path}>
                                <Link
                                    to={link.path}
                                    onClick={() => setMenuOpen(false)}
                                    aria-current={active ? 'page' : undefined}
                                    className={`block py-3 font-medium transition-colors ${active
                                        ? 'text-blue-600 dark:text-blue-400'
                                        : 'text-gray-700 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-300'
                                        }`}
                                >
                                    {link.name}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </nav>
    );
};

export default Navbar;
